package com.evoapps.evoshift.domain.usecase.daily

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.number

/**
 * SH-68: yesterday's and today's days whose takings are still missing, yesterday first, for the
 * dashboard's "Fill in the day's result?" card. Only days of a month that exists ([rosterIds]);
 * a closed day is final and a day with neither a target nor planned hours (closed for business)
 * is not asked about. A day never stored comes with its plan computed, so the card can show it.
 * SH-170: [days] other than yesterday and today (the home screen's to-do list looks at the week
 * before today).
 */
class FindMissingDailyResultsUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase
) {
    suspend operator fun invoke(
        workplaceId: String,
        rosterIds: Set<String>,
        today: LocalDate,
        days: List<LocalDate> = listOf(today.minus(DatePeriod(days = 1)), today)
    ): Result<List<DailyRecord>> = runCatching {
        days.groupBy { RosterIds.of(workplaceId, it.year, it.month.number) }
            .filterKeys { it in rosterIds }
            .flatMap { (rosterId, dates) ->
                val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().associateBy { it.date }
                val missing = dates.map { it.toString() }.filter { date ->
                    val record = stored[date]
                    record == null || (record.revenue.realNetto == null && !record.isLocked)
                }
                if (missing.isEmpty()) return@flatMap emptyList()
                val inputs = if (missing.any { it !in stored }) calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrThrow() else null
                missing.map { date -> stored[date] ?: calculateDailyRecordUseCase.compute(workplaceId, rosterId, date, inputs!!) }
            }
            .filter { it.revenue.planNetto > 0.0 || it.costs.plannedHours > 0.0 }
            .sortedBy { it.date }
    }
}
